import * as Sentry from "@sentry/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import {
  getMeetingFeedback,
  getMyFeedback,
  submitFeedback,
} from "@app/analytics/api/meetingReport";
import { MeetingFeedbackResponse } from "@app/analytics/types";
import { ResponseError } from "@app/common/api";
import useAuth from "@app/common/auth/useAuth";
import { hasRetryableStatus } from "@app/common/utils";

export interface SubmitMyFeedbackBody {
  feedback: MeetingFeedbackResponse;
}

interface UseMeetingFeedbackOpts {
  isMeetingOwner?: boolean;
  enabled?: boolean;
}

const DEFAULT_OPTS: UseMeetingFeedbackOpts = {
  isMeetingOwner: false,
  enabled: true,
};

const useMeetingFeedback = (opts = DEFAULT_OPTS) => {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const meetingFeedbackQuery = useQuery({
    queryKey: ["meetingFeedback", sessionId, user?.id],
    queryFn: () => getMeetingFeedback(sessionId!),
    enabled: Boolean(opts.isMeetingOwner) && Boolean(sessionId),
    retry: (failureCount, error: ResponseError) =>
      failureCount < 3 && hasRetryableStatus(error),
  });

  const myFeedbackQuery = useQuery({
    queryKey: ["myMeetingFeedback", sessionId, user?.id],
    queryFn: () => getMyFeedback(sessionId!),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: opts.enabled && Boolean(user) && Boolean(sessionId),
    // don't retry if user was not in the meeting
    retry: (failureCount, error: ResponseError) =>
      failureCount < 3 && hasRetryableStatus(error),
  });

  const submitMyFeedbackMutation = useMutation({
    mutationFn: ({ feedback }: SubmitMyFeedbackBody) => {
      return submitFeedback(sessionId!, { feedback });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["meetingFeedback", sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ["myMeetingFeedback", sessionId, user?.id],
      });
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });

  return {
    meetingFeedback: meetingFeedbackQuery,
    myFeedback: myFeedbackQuery,
    submitFeedback: submitMyFeedbackMutation,
  };
};

export default useMeetingFeedback;
